import { Injectable, signal } from '@angular/core';
import { ShiftId } from '@wilson/api/gateway';
import {
  ActivityRow,
  AssignmentData,
  HolidayRegionCode,
  PublicationStatus,
  Shift,
  TimelineShift,
  TimelineTimeframe,
  UserTimelines,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import { WilsonState } from '@wilson/non-domain-specific/decorators/wilson-state';
import { isDebugEnabled } from '@wilson/non-domain-specific/logger/logger';
import {
  calculateUserAssignmentStack,
  determineAssignmentsOverlaps,
} from '@wilson/non-domain-specific/overlap-helpers';
import { addDays, differenceInDays, endOfDay, startOfDay } from 'date-fns';
import { cloneDeep } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
} from 'rxjs';
import { InteractionLifeCycleCallbacks } from '../interface';

const RESOURCES_COLUMN_WIDTH_IN_PX = 288;
const DEFAULT_ROW_HEIGHT = 70;
const DEFAULT_TIMEFRAME = {
  start: startOfDay(new Date()).getTime(),
  end: endOfDay(addDays(new Date(), 6)).getTime(),
};

@Injectable()
export class AssignmentsGanttChartService {
  @WilsonState<number>(0)
  currentScrollLeftPositionSubject!: number;
  readonly currentScrollLeftPositionSubject$!: Observable<number>;

  @WilsonState<boolean>(false)
  isCellView!: boolean;
  readonly isCellView$!: Observable<boolean>;

  @WilsonState<string>('80vh')
  ganttChartHeight!: string;
  readonly ganttChartHeight$!: Observable<string>;

  @WilsonState<boolean>(false)
  isSidenavCollapsed!: boolean;
  readonly isSidenavCollapsed$!: Observable<boolean>;

  // Chart settings
  @WilsonState<number>(RESOURCES_COLUMN_WIDTH_IN_PX)
  private resourcesColumnWidthPx!: number;
  readonly resourcesColumnWidthPx$!: Observable<number>;

  @WilsonState<number>(1)
  frameLength!: number;
  readonly frameLength$!: Observable<number>;

  @WilsonState<string[]>([
    '00:00',
    '04:00',
    '08:00',
    '12:00',
    '16:00',
    '20:00',
    '24:00',
  ])
  private hoursRange!: string[];
  readonly hoursRange$!: Observable<string[]>;

  @WilsonState<number>(1)
  private zoomLevel!: number;
  readonly zoomLevel$!: Observable<number>;

  @WilsonState<boolean>(false)
  private isDragAndDropEnabled!: boolean;
  readonly isDragAndDropEnabled$!: Observable<boolean>;

  @WilsonState<Map<string, BehaviorSubject<UserTimelines>>>(new Map())
  private ganttChartUsersMap!: Map<string, BehaviorSubject<UserTimelines>>;
  ganttChartUsersMap$!: Observable<Map<string, BehaviorSubject<UserTimelines>>>;

  @WilsonState<number>(DEFAULT_ROW_HEIGHT)
  public ganttChartDefaultRowHeight!: number;
  readonly ganttChartDefaultRowHeight$!: Observable<number>;

  @WilsonState<HolidayRegionCode>([])
  private publicHolidaysRegion!: HolidayRegionCode;
  readonly publicHolidaysRegion$!: Observable<HolidayRegionCode>;

  @WilsonState<TimelineTimeframe>(DEFAULT_TIMEFRAME)
  private timeframe!: TimelineTimeframe;
  readonly timeframe$!: Observable<TimelineTimeframe>;

  @WilsonState<Record<ShiftId, ActivityRow[]>>({})
  private determinedShiftActivities!: Record<ShiftId, ActivityRow[]>;
  readonly determinedShiftActivities$!: Observable<
    Record<ShiftId, ActivityRow[]>
  >;

  @WilsonState<boolean>(false)
  private isLoadingUnassignedShifts!: boolean;
  readonly isLoadingUnassignedShifts$!: Observable<boolean>;

  @WilsonState<number>(0)
  private currentTimelineHeightPx!: number;
  readonly currentTimelineHeightPx$!: Observable<number>;
  readonly maxUnassignedRowHeightPx$ = this.currentTimelineHeightPx$.pipe(
    map((currentTimelineHeightPx) => currentTimelineHeightPx / 2),
    shareReplay(),
  );

  @WilsonState<
    (Shift &
      WithPreparedAttributes & {
        id: string;
      })[]
  >([])
  private unassignedShifts!: (Shift &
    WithPreparedAttributes & {
      id: string;
    })[];
  unassignedShifts$!: Observable<
    (Shift & WithPreparedAttributes & { id: string })[]
  >;

  @WilsonState<string[]>([])
  private unassignedShiftIdsFilter!: string[];
  private unassignedShiftIdsFilter$!: Observable<string[]>;

  @WilsonState<string[]>([])
  private unassignedShiftSearchesFilter!: string[];
  private unassignedShiftSearchesFilter$!: Observable<string[]>;

  filteredUnassignedShifts$: Observable<
    (Shift & WithPreparedAttributes & { id: string })[]
  > = combineLatest([
    this.unassignedShifts$,
    this.unassignedShiftIdsFilter$,
    this.unassignedShiftSearchesFilter$,
  ]).pipe(
    map(([unassignedShifts, unassignedShiftIds, unassignedShiftSearches]) => {
      if (unassignedShiftIds.length) {
        return unassignedShifts.filter((shift) =>
          unassignedShiftIds.includes(shift.id),
        );
      }
      if (unassignedShiftSearches.length) {
        return unassignedShifts.filter((shift) =>
          unassignedShiftSearches.some((search) =>
            shift.name.toLowerCase().includes(search.toLowerCase()),
          ),
        );
      }
      return unassignedShifts;
    }),
  );

  readonly dayCount$ = this.timeframe$.pipe(
    // Difference in days + 1 to include the end date
    map((timeframe) => differenceInDays(timeframe.end, timeframe.start) + 1),
    shareReplay(),
  );

  public assignmentData$: Observable<AssignmentData> = combineLatest([
    this.unassignedShifts$,
    this.ganttChartUsersMap$,
  ]).pipe(
    map(([unassignedShifts, ganttChartUsersMap]) => {
      const assignmentData: AssignmentData = {
        shiftAssignments: {},
        absenceAssignments: {},
        unassignedShifts: unassignedShifts.map((shift) => shift.id),
      };

      ganttChartUsersMap.forEach((userSubject) => {
        const user = userSubject.value;
        assignmentData.shiftAssignments[user.id] = Object.keys(
          user.shiftsWithoutActivitiesDictionary,
        );
        assignmentData.absenceAssignments[user.id] = Object.keys(
          user.absencesDictionary,
        );
      });

      return assignmentData;
    }),
  );

  @WilsonState<boolean>(false)
  private isUnassignedRegionExpanded!: boolean;
  readonly isUnassignedRegionExpanded$!: Observable<boolean>;

  @WilsonState<string[]>([])
  private modifiedShiftIds!: string[];
  readonly modifiedShiftIds$!: Observable<string[]>;

  public readonly defaultRowHeight = DEFAULT_ROW_HEIGHT;
  readonly defaultUnassignedSectionHeight$ = of(140);

  private _interactionLifeCycleCallbacks =
    signal<InteractionLifeCycleCallbacks>({});
  public readonly interactionLifeCycleCallbacks =
    this._interactionLifeCycleCallbacks;

  setInteractionLifeCycleCallbacks(
    callbacks: InteractionLifeCycleCallbacks,
  ): void {
    this._interactionLifeCycleCallbacks.set(Object.freeze(callbacks));
  }

  public readonly pixelWidthPerDay$ = combineLatest([
    this.frameLength$,
    this.dayCount$,
  ]).pipe(
    map(([frameLength, dayCount]) => {
      return frameLength / dayCount;
    }),
  );

  setTimeframe(timeframe: TimelineTimeframe): void {
    this.timeframe = timeframe;
  }

  setHolidaysRegion(regions: HolidayRegionCode | null | undefined): void {
    this.publicHolidaysRegion = regions || [];
  }

  setModifiedShiftIds(ids: string[]): void {
    this.modifiedShiftIds = ids;
  }

  setGanttChartUsers(users: UserTimelines[] | null): void {
    if (users) {
      const map = new Map();
      users.forEach((user) => {
        const userSubject = new BehaviorSubject<UserTimelines>(user);
        map.set(user.id as string, userSubject);
      });
      this.ganttChartUsersMap = map;
    }
  }

  setDeterminedShiftActivities(
    value: Record<ShiftId, ActivityRow[]> | null,
  ): void {
    if (value) {
      this.determinedShiftActivities = value;
    }
  }

  setUnassignedShifts(
    shifts:
      | (Shift &
          WithPreparedAttributes & {
            id: string;
          })[]
      | null,
  ): void {
    this.unassignedShifts = shifts || [];
  }

  setTimelineHeightPx(value: number): void {
    this.currentTimelineHeightPx = value;
  }

  toggleUnassignedRegion(isRegionExpanded: boolean): void {
    this.isUnassignedRegionExpanded = isRegionExpanded;
  }

  async upsertShiftToUser(shift: TimelineShift, userId: string): Promise<void> {
    const userSubject = this.ganttChartUsersMap.get(userId);

    if (userSubject) {
      const userToUpdate = cloneDeep(await firstValueFrom(userSubject));
      userToUpdate.shiftsWithoutActivitiesDictionary[shift.id] = shift;
      if (this.isCellView) {
        userToUpdate.assignmentStacks = calculateUserAssignmentStack(
          userToUpdate,
          isDebugEnabled(),
        );
      } else {
        userToUpdate.determinedRowDataV2 = determineAssignmentsOverlaps({
          shifts: Object.values(userToUpdate.shiftsWithoutActivitiesDictionary),
          absences: Object.values(userToUpdate.absencesDictionary),
          stays: Object.values(userToUpdate.staysDictionary),
          isDebugEnabled: isDebugEnabled(),
        });
      }

      userSubject.next({ ...userToUpdate });
    }
  }

  async updateShiftPublicationStatus(
    shiftId: string,
    userId: string,
    status: PublicationStatus,
  ): Promise<void> {
    const userSubject = this.ganttChartUsersMap.get(userId);

    if (userSubject) {
      const userToUpdate = cloneDeep(await firstValueFrom(userSubject));

      if (userToUpdate.shiftsWithoutActivitiesDictionary[shiftId]) {
        userToUpdate.shiftsWithoutActivitiesDictionary[
          shiftId
        ].publicationStatus = status;
        userSubject.next({ ...userToUpdate });
      }
    }
  }

  async removeShiftFromUser(shiftId: string, userId: string): Promise<void> {
    const userSubject = this.ganttChartUsersMap.get(userId);

    if (userSubject) {
      const userToUpdate = cloneDeep(await firstValueFrom(userSubject));

      if (userToUpdate.shiftsWithoutActivitiesDictionary[shiftId]) {
        delete userToUpdate.shiftsWithoutActivitiesDictionary[shiftId];
        if (this.isCellView) {
          userToUpdate.assignmentStacks = calculateUserAssignmentStack(
            userToUpdate,
            isDebugEnabled(),
          );
        } else {
          userToUpdate.determinedRowDataV2 = determineAssignmentsOverlaps({
            shifts: Object.values(
              userToUpdate.shiftsWithoutActivitiesDictionary,
            ),
            absences: Object.values(userToUpdate.absencesDictionary),
            stays: Object.values(userToUpdate.staysDictionary),
            isDebugEnabled: isDebugEnabled(),
          });
        }
        userSubject.next({ ...userToUpdate });
      }
    }
  }

  setSideNavCollapsedState(isCollapsed: boolean): void {
    this.isSidenavCollapsed = isCollapsed;
  }

  addShiftToUnassignedShifts(
    shift: Shift &
      WithPreparedAttributes & {
        id: string;
      },
  ): void {
    const unassignedShifts = this.unassignedShifts;
    if (unassignedShifts) {
      unassignedShifts.push(shift);
      this.unassignedShifts = [...unassignedShifts];
    }
  }

  removeShiftFromUnassignedShifts(shiftId: string): void {
    let unassignedShifts = this.unassignedShifts;
    if (unassignedShifts) {
      unassignedShifts = unassignedShifts.filter((s) => s.id !== shiftId);
      this.unassignedShifts = [...unassignedShifts];
    }
  }

  setUnassignedShiftIdsFilter(ids: string[]): void {
    this.unassignedShiftIdsFilter = ids;
  }

  setUnassignedShiftSearchesFilter(searches: string[]): void {
    this.unassignedShiftSearchesFilter = searches;
  }

  setDragAndDropEnabled(isEnabled: boolean): void {
    this.isDragAndDropEnabled = isEnabled;
  }
}
